import React, { createContext, useContext, useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { authAPI, systemAPI } from '../services/api';

const AuthContext = createContext();

export { AuthContext };

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    const token = Cookies.get('access_token');

    if (token) {
      try {
        const response = await authAPI.getMe();
        setUser(response.data);
        
        // Fetch user permissions for multi-branch access
        try {
          const permissionsResponse = await systemAPI.getPermissions();
          setPermissions(permissionsResponse.data);
        } catch (permError) {
          console.error('Failed to fetch permissions:', permError);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        Cookies.remove('access_token');
      }
    }
    setLoading(false);
  };

  const login = async (username, password) => {
    try {
      const response = await authAPI.login(username, password);
      const { access_token, user: userData } = response.data;
      
      Cookies.set('access_token', access_token, { 
        expires: 31, // 31 days to match JWT expiration
        path: '/', // Available on all pages
        sameSite: 'Lax', // CSRF protection
        secure: window.location.protocol === 'https:' // Secure on HTTPS only
      });

      setUser(userData);
      
      // Fetch permissions after successful login
      try {
        const permissionsResponse = await systemAPI.getPermissions();
        setPermissions(permissionsResponse.data);
      } catch (permError) {
        console.error('Failed to fetch permissions:', permError);
      }
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const logout = () => {
    Cookies.remove('access_token');
    setUser(null);
    setPermissions(null);
  };

  const refreshUser = async () => {
    const token = Cookies.get('access_token');
    if (token) {
      try {
        const response = await authAPI.getMe();
        setUser(response.data);
        
        // Refresh permissions as well
        try {
          const permissionsResponse = await systemAPI.getPermissions();
          setPermissions(permissionsResponse.data);
        } catch (permError) {
          console.error('Failed to fetch permissions:', permError);
        }
        return { success: true };
      } catch (error) {
        console.error('Failed to refresh user:', error);
        return { success: false };
      }
    }
    return { success: false };
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      return { success: true, data: response.data };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const hasRole = (roles) => {
    if (!user) return false;
    if (Array.isArray(roles)) {
      return roles.includes(user.role);
    }
    return user.role === roles;
  };

  const canAccessBranch = (branchId) => {
    if (!user) return false;
    // Admins, managers, and supervisors can access all branches
    if (user.role === 'admin' || user.role === 'store_manager' || user.role === 'manager_assistant' || user.role === 'makeup_supervisor' || user.role === 'photographer_supervisor' || user.role === 'retoucher_supervisor') return true;
    if (!permissions) return false;
    return permissions.accessible_branches.includes(branchId);
  };

  const canManageBranch = (branchId) => {
    if (!user) return false;
    // Admins, managers, and supervisors can manage all branches
    if (user.role === 'admin' || user.role === 'store_manager' || user.role === 'manager_assistant' || user.role === 'makeup_supervisor' || user.role === 'photographer_supervisor' || user.role === 'retoucher_supervisor') return true;
    if (!permissions) return false;
    return permissions.manageable_branches.includes(branchId);
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    // Admins, managers, and supervisors have all permissions
    if (user.role === 'admin' || user.role === 'store_manager' || user.role === 'manager_assistant' || user.role === 'makeup_supervisor' || user.role === 'photographer_supervisor' || user.role === 'retoucher_supervisor') return true;
    if (!permissions) return false;
    return permissions.permissions[permission] === true;
  };

  // Helper function to get all accessible branches for admins and managers
  const getAllAccessibleBranches = () => {
    if (!user) return [];
    // For admins, managers, and supervisors, we need to return all branches
    // This will be populated when branches are fetched
    if (user.role === 'admin' || user.role === 'store_manager' || user.role === 'manager_assistant' || user.role === 'makeup_supervisor' || user.role === 'photographer_supervisor' || user.role === 'retoucher_supervisor') {
      return 'ALL_BRANCHES'; // Special marker that components can check for
    }
    return permissions?.accessible_branches || [];
  };

  const getAllManageableBranches = () => {
    if (!user) return [];
    // For admins, managers, and supervisors, we need to return all branches
    if (user.role === 'admin' || user.role === 'store_manager' || user.role === 'manager_assistant' || user.role === 'makeup_supervisor' || user.role === 'photographer_supervisor' || user.role === 'retoucher_supervisor') {
      return 'ALL_BRANCHES'; // Special marker that components can check for
    }
    return permissions?.manageable_branches || [];
  };

  const value = {
    user,
    setUser,
    permissions,
    loading,
    login,
    logout,
    refreshUser,
    register,
    hasRole,
    canAccessBranch,
    canManageBranch,
    hasPermission,
    isAuthenticated: !!user,
    // Branch-related user info
    homeBranchId: user?.home_branch_id,
    isMoveable: user?.is_moveable,
    holidayGroupId: user?.holiday_group_id,
    accessibleBranches: getAllAccessibleBranches(),
    manageableBranches: getAllManageableBranches()
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};